/**
 * Module-level record of quest progress the player has already earned locally.
 *
 * Both facts are decided on the client and are authoritative for the UI:
 *  - the ad quest is finished the moment the ad has been shown;
 *  - the reward is claimed the moment the player taps the gift.
 *
 * Several independent writers fill the quest query cache — the modal's own
 * `allQuests` query and `prefetchAllQuests`, which the `quests:invalidate`
 * socket event triggers — and `/user/quests/assign-and-get` has been measured at
 * 6-7s in production. A response that left the server BEFORE one of these local
 * events therefore lands AFTER it, still carrying `is_finished: false` /
 * `reward_claimed: false`, and resets the card. With several slow responses in
 * flight the card flips once per landing response.
 *
 * Keeping these sets at module scope — rather than in a component ref — is what
 * lets every cache writer apply the same protection.
 */
export const optimisticallyCompletedAdQuests = new Set<number>();
export const optimisticallyClaimedRewards = new Set<number>();

export function markAdQuestFinishedLocally(userQuestId: number): void {
  optimisticallyCompletedAdQuests.add(userQuestId);
}

export function markRewardClaimedLocally(userQuestId: number): void {
  optimisticallyClaimedRewards.add(userQuestId);
}

export function unmarkRewardClaimedLocally(userQuestId: number): void {
  optimisticallyClaimedRewards.delete(userQuestId);
}

interface OptimisticQuestShape {
  user_quest_id?: number | null;
  is_finished?: boolean;
  reward_claimed?: boolean;
  objectives?: Array<{
    objective_id?: number;
    type?: string;
    current_count?: number;
    target_count?: number;
    is_completed?: boolean;
  }> | null;
}

/**
 * Re-applies locally-earned quest state on top of a server snapshot, so a stale
 * response cannot undo an ad the player watched or a reward they collected.
 */
export function preserveOptimisticQuestState<T extends OptimisticQuestShape>(quests: T[]): T[] {
  if (optimisticallyCompletedAdQuests.size === 0 && optimisticallyClaimedRewards.size === 0) {
    return quests;
  }

  return quests.map((quest) => {
    const id = quest.user_quest_id;
    if (id == null) return quest;

    let next = quest;

    if (optimisticallyCompletedAdQuests.has(id)) {
      next = {
        ...next,
        is_finished: true,
        objectives: next.objectives?.map((objective) =>
          objective.type === 'watch_ad' || objective.type === 'watch_ads'
            ? { ...objective, current_count: objective.target_count, is_completed: true }
            : objective,
        ) ?? next.objectives,
      };
    }

    if (optimisticallyClaimedRewards.has(id)) {
      next = { ...next, reward_claimed: true };
    }

    return next;
  });
}

/**
 * Single durable answer to "has this reward already been collected?".
 *
 * Before this existed the answer was spread over three volatile stores and the
 * card could disagree with itself between two renders one second apart:
 *   - the query cache — only tells the truth once a response actually lands, so
 *     it still says `reward_claimed: false` while nothing has come back yet;
 *   - `claimedQuestIds` — React state, gone the moment the modal unmounts;
 *   - `globalClaimedQuestTimestamps` — module level, but expires after 10s and
 *     is deleted as soon as the server confirms.
 *
 * `optimisticallyClaimedRewards` is module level and permanent for the session,
 * so consulting it here makes the claimed state survive a remount, a cache that
 * is never rewritten, and the expiry of the 10s window alike. A genuine claim
 * failure calls unmarkRewardClaimedLocally() and the card unlocks again.
 */
export function isRewardClaimed(
  userQuestId: number | null | undefined,
  serverSaysClaimed: boolean | undefined,
  claimedQuestIds?: { has(id: number): boolean },
): boolean {
  if (serverSaysClaimed) return true;
  if (userQuestId == null) return false;
  if (optimisticallyClaimedRewards.has(userQuestId)) return true;
  return claimedQuestIds?.has(userQuestId) ?? false;
}
